import { Badge, Col, Row } from 'react-bootstrap';

import { orderLineDetails } from '../../helpers/orderProducts';

// What an order is actually for, in the detail that makes it worth opening.
//
// One component, two homes: the row that opens under the orders table and the
// modal on the order card. They were asked for the same content, and two
// copies of it would be two things to keep in step — this app has paid for
// that lesson more than once.
//
// Per LINE rather than per cable. Two lines of the same cable can be a drum
// line and a coil line with different due dates, and rolling them together
// hides the only thing worth looking at.
//
// Laid out side by side rather than stacked. ORD-1791094684232 has five lines;
// in a column that is a wall of text taller than the row it opens under, and
// the order below it is pushed off the screen. The breakpoints do the work:
// two across in the card modal, three in the full-width table row, one on a
// phone. Each line is boxed so the eye can tell where one ends — without a
// border the figures of one run into the name of the next.
//
// The progress figures are `orderLineDetails`', which is OrdersMonitorTable's
// arithmetic moved into a helper rather than re-derived: a second way of
// computing "remaining" is a second answer to one question.

const fmt = n => Math.round(Number(n) || 0).toLocaleString();

const OrderCablesDetail = ({ order }) => {
  const lines = orderLineDetails(order);
  if (!lines.length) {
    return <div className="text-700 fs-9 text-center py-3">No cable lines on this order.</div>;
  }

  return (
    <Row className="g-2 py-3">
      {lines.map(l => (
        <Col key={l.lineId} xs={12} md={6} xl={4}>
          <div className="h-100 border border-translucent rounded-2 px-2 py-2 text-center">
            <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap mb-1">
              <span className="fw-bold fs-9">{l.label}</span>
              {/* Short, because the cell is narrow now — "15,000 m ordered"
                  wraps and pushes the figures down a line. Unambiguous all the
                  same: it sits against the cable name, and the three figures
                  below are each labelled. */}
              <Badge bg="info" className="fs-10 fw-bold" title="Ordered quantity">
                {fmt(l.ordered)} m
              </Badge>
            </div>

            {/* The delivery plan: what it is to be made AS. A quantity with no
                drum type is not something anybody can fetch from the yard. */}
            {l.items.length > 0 && (
              <div className="d-flex justify-content-center gap-1 flex-wrap mb-1">
                {l.items.map((it, i) => (
                  <Badge
                    key={i}
                    bg={it.kind === 'DRUM' ? 'primary' : 'success'}
                    className="fs-10"
                    title={
                      it.produced > 0
                        ? `${it.produced} of ${it.qty} already made`
                        : 'none made yet'
                    }
                  >
                    {it.qty} × {it.spec}
                    {it.len ? ` @ ${fmt(it.len)} m` : ''}
                    {it.produced > 0 ? ` · ${it.produced} made` : ''}
                  </Badge>
                ))}
              </div>
            )}

            {/* The same four figures the orders monitor shows, in the same
                colours, so the two screens cannot look like they disagree. */}
            <div className="d-flex justify-content-center gap-2 flex-wrap fs-10">
              <span className="text-success fw-semibold">● Prod {fmt(l.produced)} m</span>
              <span className="text-primary fw-semibold">● Alloc {fmt(l.allocated)} m</span>
              {l.fromStock > 0 && (
                <span className="fw-semibold" style={{ color: '#12b886' }}>
                  ● Stock {fmt(l.fromStock)} m
                </span>
              )}
              {l.remaining > 0 && (
                <span className="text-warning fw-semibold">○ Rem {fmt(l.remaining)} m</span>
              )}
            </div>
          </div>
        </Col>
      ))}
    </Row>
  );
};

export default OrderCablesDetail;
